import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CalendarClock, CalendarDays, ChevronDown, CircleAlert, CircleCheck, CircleX, PauseCircle, Settings2, TriangleAlert } from "lucide-react";
import { Button } from "@akuna/ui";
import { StayPlanUsageReservationDialog } from "@/components/packages/StayPlanUsageReservationDialog";
import { StayPlanBalanceAdjustmentDialog } from "@/components/packages/StayPlanBalanceAdjustmentDialog";
import { StayPlanAdjustmentHistory } from "@/components/packages/StayPlanAdjustmentHistory";
import { StayPlanConfigDialog } from "@/components/packages/StayPlanConfigDialog";
import { EditPlanDaysDialog } from "@/components/packages/plan-card/EditPlanDaysDialog";
import { PlanAdjustRow } from "@/components/packages/plan-card/PlanAdjustRow";
import { PlanBalanceMeter } from "@/components/packages/plan-card/PlanBalanceMeter";
import { PlanBillingPanel } from "@/components/packages/plan-card/PlanBillingPanel";
import { PlanCompactMeter } from "@/components/packages/plan-card/PlanCompactMeter";
import { PlanPreviousCycles } from "@/components/packages/plan-card/PlanPreviousCycles";
import { PlanUsageTimeline } from "@/components/packages/plan-card/PlanUsageTimeline";
import { buildPlanMeter } from "@/components/packages/plan-card/planPips";
import { resolvePlanHeaderPeriod } from "@/components/packages/plan-card/planHeader";
import { PLAN_TONE } from "@/components/packages/plan-card/planTone";
import { useChargeStayPlanNow } from "@/hooks/useChargeStayPlanNow";
import { usePlanUsageDetails } from "@/hooks/usePlanUsageDetails";
import { buildStayPlanStatus } from "@/hooks/useStayPlanStatus";
import type { StayPlanAdjustment } from "@/hooks/useStayPlanAdjustments";
import type { StayPlanContract } from "@/hooks/useStayPlanContracts";
import { formatDateBR, getBrazilDate } from "@/lib/date-utils";
import { WEEKDAY_KEYS } from "@/pages/daycare-hotel/plans/FixedWeekdaysField";

export interface PlanCardProps {
  contract: StayPlanContract;
  petName: string;
  /**
   * Card recolhido: só o cabeçalho e o saldo em barras, como o segundo plano
   * do design. O usuário abre o detalhe pelo chevron.
   */
  compact?: boolean;
}

function formatCurrency(value: number, language: string) {
  return new Intl.NumberFormat(language.startsWith("pt") ? "pt-BR" : "en-US", {
    style: "currency",
    currency: "BRL",
  }).format(value);
}

function daysUntil(target: string, today: string) {
  const diff = Date.parse(`${target}T00:00:00Z`) - Date.parse(`${today}T00:00:00Z`);
  return Math.round(diff / 86_400_000);
}

const STATUS_TONE: Record<string, string> = {
  active: "border-state-success/30 bg-state-success-soft text-state-success-foreground",
  paused: "border-state-warning/40 bg-state-warning-soft text-state-warning-foreground",
  cancelled: "border-border bg-muted text-muted-foreground",
};

const STATUS_ICON = { active: CircleCheck, paused: PauseCircle, cancelled: CircleX };

export function PlanCard({ contract, petName, compact = false }: PlanCardProps) {
  const { t, i18n } = useTranslation("patients");
  const navigate = useNavigate();
  const location = useLocation();
  const chargeNow = useChargeStayPlanNow();

  const [expanded, setExpanded] = useState(!compact);
  const [selectedUsageId, setSelectedUsageId] = useState<string | null>(null);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustDirection, setAdjustDirection] = useState<"give" | "remove">("give");
  const [reversing, setReversing] = useState<StayPlanAdjustment | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  const [configSection, setConfigSection] = useState<"terms" | "billing">("terms");
  const [editDaysOpen, setEditDaysOpen] = useState(false);

  const today = getBrazilDate();
  const status = useMemo(() => buildStayPlanStatus(contract, today), [contract, today]);
  const meter = useMemo(() => buildPlanMeter(status), [status]);
  const tone = PLAN_TONE[contract.module];
  const selectedUsage = contract.usages.find((usage) => usage.id === selectedUsageId) ?? null;

  const cycleUsages = useMemo(
    () =>
      status.cycle
        ? contract.usages.filter((usage) => usage.usedOn >= status.cycle!.start && usage.usedOn <= status.cycle!.end)
        : contract.usages,
    [contract.usages, status.cycle],
  );

  // Turno, horários e matilha de cada diária. Sem isso a timeline seria só uma
  // lista de datas.
  const usageDetails = usePlanUsageDetails(contract.id, expanded ? cycleUsages : []);

  const isOpen = contract.status === "active" || contract.status === "paused";
  const isOverdue = status.paymentStatus === "overdue";
  const overdueDays = isOverdue && status.dueOn ? Math.max(0, -daysUntil(status.dueOn, today)) : 0;
  const renewsIn = status.cycle && contract.status === "active" ? daysUntil(status.cycle.end, today) + 1 : null;
  const headerPeriod = resolvePlanHeaderPeriod(contract, status.cycle);
  const StatusIcon = STATUS_ICON[contract.status as keyof typeof STATUS_ICON] ?? CircleX;
  // A RPC de troca de dias só aceita matrícula de creche ativa.
  const canEditDays =
    contract.module === "daycare" && contract.usageRule === "per_week" && contract.status === "active";

  const openConfig = (section: "terms" | "billing") => {
    setConfigSection(section);
    setConfigOpen(true);
  };

  const openAdjust = (direction: "give" | "remove") => {
    setReversing(null);
    setAdjustDirection(direction);
    setAdjustOpen(true);
  };

  const runChargeNow = async () => {
    const saleId = await chargeNow.mutateAsync({ contract, today });
    navigate("/sales/new", {
      state: {
        resumeSale: {
          saleId,
          context: "planCharge",
          returnTo: `${location.pathname}${location.search}`,
        },
      },
    });
  };

  return (
    <article
      data-testid="plan-card"
      className={`overflow-hidden rounded-card border bg-background shadow-akuna-xs ${
        isOverdue ? "border-state-danger/30" : "border-border"
      }`}
    >
      {isOverdue ? (
        <div className="flex flex-wrap items-center gap-2 bg-state-danger-soft px-4 py-2.5 sm:px-6">
          <TriangleAlert className="h-4 w-4 shrink-0 text-state-danger" aria-hidden />
          <p className="text-xs font-semibold text-state-danger-foreground">
            {t("planCard.overdueStrip", { count: overdueDays })}
          </p>
          <Button
            type="button"
            size="sm"
            className="ms-auto !bg-state-danger !text-primary-foreground"
            disabled={chargeNow.isPending}
            onClick={() => void runChargeNow()}
          >
            {t("planCard.resolvePayment")}
          </Button>
        </div>
      ) : null}

      <div
        className={`flex flex-col md:flex-row flex-wrap items-center gap-3 px-4 py-4 sm:px-6 ${
          expanded ? "border-b border-border" : ""
        }`}
      >
        <span
          className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-[14px] ${tone.tile}`}
          aria-hidden
        >
          <tone.Icon className="h-5 w-5" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h4 className="font-display text-lg font-bold text-foreground">{contract.planName}</h4>
            <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tone.chip}`}>
              {t(contract.module === "daycare" ? "stayPlans.moduleDaycare" : "stayPlans.moduleHotel")}
            </span>
            {isOverdue ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-state-danger/30 bg-state-danger-soft px-2.5 py-0.5 text-xs font-semibold text-state-danger-foreground">
                <CircleAlert className="h-3.5 w-3.5" aria-hidden />
                {t("planCard.paymentOverdue")}
              </span>
            ) : (
              <span
                className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${
                  STATUS_TONE[contract.status] ?? STATUS_TONE.cancelled
                }`}
              >
                <StatusIcon className="h-3.5 w-3.5" aria-hidden />
                {contract.status === "active"
                  ? t("daycarePlans.statusActive")
                  : contract.status === "paused"
                    ? t("planCard.statusPaused")
                    : t("daycarePlans.statusEnded")}
              </span>
            )}
            {isOpen ? (
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                <CalendarClock className="h-3.5 w-3.5" aria-hidden />
                {t(contract.billingTiming === "end" ? "planCard.billingTagEnd" : "planCard.billingTagStart")}
              </span>
            ) : null}
          </div>

          <p className="mt-1 text-[0.8125rem] text-muted-foreground">
            {contract.kind === "recurring"
              ? t("planCard.pricePerMonth", { amount: formatCurrency(contract.price, i18n.language) })
              : formatCurrency(contract.price, i18n.language)}
            {headerPeriod.kind === "range"
              ? ` · ${t("stayPlans.cycleRange", {
                  start: formatDateBR(headerPeriod.start),
                  end: formatDateBR(headerPeriod.end),
                })}`
              : ` · ${t("daycarePlans.startsOn")} ${formatDateBR(headerPeriod.start)}`}
            {contract.kind === "day_package" ? ` · ${t("planCard.noExpiry")}` : ""}
            {renewsIn !== null && renewsIn > 0 ? ` · ${t("planCard.renewsIn", { count: renewsIn })}` : ""}
          </p>
        </div>

        <div className="ms-auto flex items-center gap-3">
          {!expanded ? <PlanCompactMeter status={status} /> : null}
          {isOpen && expanded ? (
            <Button type="button" size="sm" variant="outline" onClick={() => openConfig("terms")}>
              <Settings2 className="me-2 h-4 w-4" aria-hidden />
              {t("planCard.configure")}
            </Button>
          ) : null}
          {compact ? (
            <Button
              type="button"
              size="icon-sm"
              variant="outline"
              aria-expanded={expanded}
              aria-label={t(expanded ? "planCard.collapse" : "planCard.expand")}
              onClick={() => setExpanded((open) => !open)}
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} aria-hidden />
            </Button>
          ) : null}
        </div>
      </div>

      {expanded ? (
        <>
          <div className="grid lg:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)]">
            <div className="border-b border-border p-4 sm:p-6 lg:border-b-0 lg:border-e">
              {contract.usageRule === "per_week" ? (
                <div className="mb-4 flex flex-col gap-1">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    {contract.fixedWeekdays.length > 0 ? (
                      <div className="flex flex-wrap items-center gap-1.5">
                        {contract.fixedWeekdays.map((weekday) => (
                          <span
                            key={weekday}
                            className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground"
                          >
                            {t(WEEKDAY_KEYS[weekday], { ns: "daycareHotel" })}
                          </span>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-muted-foreground">{t("planCard.fixedDays.pending")}</p>
                    )}
                    {canEditDays ? (
                      <Button type="button" variant="ghost" size="sm" onClick={() => setEditDaysOpen(true)}>
                        <CalendarDays className="h-4 w-4" aria-hidden />
                        {t("planCard.editDays.open")}
                      </Button>
                    ) : null}
                  </div>
                  {contract.daysReviewNeeded ? (
                    <p
                      role="status"
                      aria-label={t("planCard.fixedDays.reviewLabel")}
                      className="text-xs text-state-warning-foreground"
                    >
                      {t("planCard.fixedDays.review", {
                        contracted: contract.usageLimit ?? 0,
                        defined: contract.fixedWeekdays.length,
                      })}
                    </p>
                  ) : null}
                </div>
              ) : null}
              <PlanBalanceMeter status={status} carryoverEnabled={contract.carryoverEnabled} />
            </div>
            <div className="flex flex-col p-4 sm:p-6">
              <PlanBillingPanel
                status={status}
                charging={chargeNow.isPending}
                onChargeNow={() => void runChargeNow()}
                onChangeBilling={() => openConfig("billing")}
              />
              <PlanPreviousCycles charges={contract.charges} currentCycleStart={status.cycle?.start ?? null} />
            </div>
          </div>

          <div className="border-t border-border p-4 sm:p-6">
            <PlanUsageTimeline
              usages={cycleUsages}
              total={meter && meter.kind !== "week" ? meter.total : null}
              details={usageDetails.data ?? new Map()}
              onOpenUsage={(usage) => setSelectedUsageId(usage.id)}
            />

            {isOpen ? <PlanAdjustRow onGive={() => openAdjust("give")} onRemove={() => openAdjust("remove")} /> : null}

            <StayPlanAdjustmentHistory
              contract={contract}
              onReverse={(adjustment) => {
                setReversing(adjustment);
                setAdjustOpen(true);
              }}
            />
          </div>
        </>
      ) : null}

      {adjustOpen ? (
        <StayPlanBalanceAdjustmentDialog
          contract={contract}
          petName={petName}
          open={adjustOpen}
          onOpenChange={(open) => {
            setAdjustOpen(open);
            if (!open) setReversing(null);
          }}
          reverses={reversing}
          defaultDirection={adjustDirection}
        />
      ) : null}

      {configOpen ? (
        <StayPlanConfigDialog
          contract={contract}
          open={configOpen}
          onOpenChange={setConfigOpen}
          initialSection={configSection}
        />
      ) : null}

      {canEditDays ? (
        <EditPlanDaysDialog contract={contract} open={editDaysOpen} onOpenChange={setEditDaysOpen} />
      ) : null}

      <StayPlanUsageReservationDialog
        usage={selectedUsage}
        open={Boolean(selectedUsage)}
        onOpenChange={(open) => !open && setSelectedUsageId(null)}
      />
    </article>
  );
}
